import React from 'react';
import moment from 'moment';
import strings from 'utils/strings';
import Logo from '../../../assets/dysmate-logo-cropped.jpg';
import HorizontalBarChart from 'components/HorizontalBarChart';

import {
	TEST_STEPS_BY_ID,
	YOUTH_TEST_STEPS_BY_ID,
	CHILD_SCRNG_TEST_STEPS_BY_ID,
} from 'constants/index';

const getNumericValue = (value) => {
	const n = Number(value);
	return Number.isFinite(n) ? n : null;
};

const getDisplayTScore = (subtest) => {
	const directTScore = getNumericValue(subtest.tScore);

	if (directTScore !== null) {
		return Math.round(directTScore * 10) / 10;
	}

	const zScore = getNumericValue(subtest.zScore);

	if (zScore === null) {
		return null;
	}

	// Standard T-score formula
	return Math.round((50 + zScore * 10) * 10) / 10;
};

const valueOrDash = (value) => {
	return value === undefined || value === null || value === '' ? '-' : value;
};

const defaultReportOptions = {
	includeCover: true,
	includeSummary: true,
	includeReportText: false,
	includeDetailedAnswers: true,
	includeSubtestCharts: true,
	includeComments: true,
};

const getReportTitle = (testType) => {
	switch (testType) {
		case 'adult-screening':
			return strings.testManagement.dysmateScreeningtestVuxna;
		case 'youth-screening':
			return strings.testManagement.dysmateScreeningtestUngdomar;
		case 'child-screening':
			return strings.testManagement.dysmateScreeningtestBarn;
		default:
			return strings.testManagement.dysmateScreeningtest;
	}
};

const getSubtestInfo = (testType, subtestId) => {
	const id = Number(subtestId);

	switch (testType) {
		case 'adult-screening':
			return TEST_STEPS_BY_ID[id] || {};
		case 'youth-screening':
			return YOUTH_TEST_STEPS_BY_ID[id] || {};
		case 'child-screening':
			return CHILD_SCRNG_TEST_STEPS_BY_ID[id] || {};
		default:
			return {};
	}
};

const getSubtestName = (subtest, testType) => {
	const subtestInfo = getSubtestInfo(testType, subtest.id);

	return (
		subtestInfo.label ||
		subtestInfo.name ||
		subtest.label ||
		subtest.name ||
		subtest.title ||
		`Deltest ${subtest.id}`
	);
};

const getChoiceText = (answer) => {
	if (!answer || !answer.choices || !answer.value) {
		return '';
	}

	const choice = answer.choices[answer.value];

	if (!choice) {
		return answer.value;
	}

	return choice.choice || choice.text || answer.value;
};

const getCorrectChoiceText = (answer) => {
	if (!answer || !answer.choices || !answer.correctValues?.[0]) {
		return answer.correctValues?.[0] || '-';
	}

	const correctKey = answer.correctValues[0];
	const choice = answer.choices[correctKey];

	if (!choice) {
		return correctKey;
	}

	return choice.choice || choice.text || correctKey;
};

const renderResultBadge = (isCorrect) => {
	return (
		<span
			className={
				isCorrect
					? 'customer-report-result-badge is-correct'
					: 'customer-report-result-badge is-wrong'
			}
			title={isCorrect ? strings.common.ratt : strings.common.fel}
		>
			{isCorrect ? '✓' : '✕'}
		</span>
	);
};

const ReportAnswerTable = ({subtest, testType}) => {
	const answers = subtest.formattedAnswers || [];

	if (!answers.length) {
		return null;
	}

	if (subtest.id === 4) {
		return (
			<div className="customer-report-writing">
				<h4>{strings.testView.candidatetext}</h4>
				<ol>
					{answers.map((answerLine, index) => (
						<li key={index}>
							{answerLine.map((part) => part.value).join(' ')}
						</li>
					))}
				</ol>
			</div>
		);
	}

	if ([5, 6].includes(Number(subtest.id))) {
		return (
			<table className="customer-report-table customer-report-answer-table customer-report-question-answer-table">
				<thead>
					<tr>
						<th>{strings.common.nr}</th>
						<th>{strings.common.question}</th>
						<th>{strings.common.answer}</th>
						<th>{strings.testView.correctanswer}</th>
						<th>{strings.common.result}</th>
					</tr>
				</thead>
				<tbody>
					{answers.map((answer) => (
						<tr key={answer.id}>
							<td>{answer.id}</td>
							<td>{answer.question || '-'}</td>
							<td>{getChoiceText(answer) || '(inget svar)'}</td>
							<td>{getCorrectChoiceText(answer)}</td>
							<td>{renderResultBadge(answer.isCorrect)}</td>
						</tr>
					))}
				</tbody>
			</table>
		);
	}

	return (
		<table className="customer-report-table customer-report-answer-table">
			<thead>
				<tr>
					<th>{strings.common.nr}</th>
					<th>{strings.common.answer}</th>
					<th>{strings.testView.correctanswer}</th>
					<th>{strings.common.result}</th>
				</tr>
			</thead>
			<tbody>
				{answers.map((answer) => (
					<tr key={answer.id}>
						<td>{answer.id}</td>
						<td>{answer.value || '(inget svar)'}</td>
						<td>{answer.correctValues?.[0] || '-'}</td>
						<td>{renderResultBadge(answer.isCorrect)}</td>
					</tr>
				))}
			</tbody>
		</table>
	);
};

const toNumber = (value) => {
	const number = Number(value);
	return Number.isFinite(number) ? number : null;
};

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const getZAssessment = (zScore) => {
	const z = toNumber(zScore);

	if (z === null) {
		return strings.testView.bedomningsSaknas;
	}

	if (z <= -1) {
		return strings.testView.underGenomsnittsomradet;
	}

	if (z >= 1) {
		return strings.testView.overGenomsnittsomradet;
	}

	return strings.testView.inomNormalomradet;
};

const getZAssessmentClass = (zScore) => {
	const z = toNumber(zScore);

	if (z === null) {
		return 'is-neutral';
	}

	if (z <= -1) {
		return 'is-low';
	}

	if (z >= 1) {
		return 'is-high';
	}

	return 'is-normal';
};

const MiniZChart = ({zScore, tScore}) => {
	const z = toNumber(zScore);
	const t = toNumber(tScore);

	if (z === null) {
		return null;
	}

	const displayT = t !== null ? t : 50 + z * 10;

	const width = 680;
	const height = 230;

	const leftPad = 64;
	const rightPad = 64;
	const curveBottom = 145;
	const curveTop = 24;

	const chartMinZ = -4;
	const chartMaxZ = 4;
	const usableWidth = width - leftPad - rightPad;

	const xForZ = (value) =>
		leftPad + ((value - chartMinZ) / (chartMaxZ - chartMinZ)) * usableWidth;

	const markerZ = clamp(z, chartMinZ, chartMaxZ);
	const markerX = xForZ(markerZ);

	// Bell curve path
	const points = [];
	for (let currentZ = chartMinZ; currentZ <= chartMaxZ; currentZ += 0.1) {
		const normalized = Math.exp(-0.5 * currentZ * currentZ);
		const x = xForZ(currentZ);
		const y = curveBottom - normalized * (curveBottom - curveTop);
		points.push(`${x},${y}`);
	}
	const curvePath = `M ${points.join(' L ')}`;

	// Main z-score ticks
	const zTicks = [-3, -2, -1, 0, 1, 2, 3];

	// T-score axis values
	const tTicks = [
		{z: -3, t: 20},
		{z: -2, t: 30},
		{z: -1, t: 40},
		{z: 0, t: 50},
		{z: 1, t: 60},
		{z: 2, t: 70},
		{z: 3, t: 80},
	];

	return (
		<div className="customer-report-mini-z-chart">
			<svg
				viewBox={`0 0 ${width} ${height}`}
				className="customer-report-mini-z-chart__svg"
				role="img"
				aria-label={`Z-score ${z.toFixed(2)}, T-score ${displayT.toFixed(1)}`}
			>
				{/* Bell curve */}
				<path
					d={curvePath}
					fill="none"
					stroke="#4b5563"
					strokeWidth="2"
				/>

				{/* Base line under curve */}
				<line
					x1={leftPad}
					y1={curveBottom}
					x2={width - rightPad}
					y2={curveBottom}
					stroke="#6b7280"
					strokeWidth="1.5"
				/>

				{/* Vertical separators at z ticks */}
				{zTicks.map((tick) => {
					const x = xForZ(tick);
					return (
						<line
							key={`z-line-${tick}`}
							x1={x}
							y1={curveBottom}
							x2={x}
							y2={curveBottom - 42}
							stroke="#9ca3af"
							strokeWidth="1"
						/>
					);
				})}

				{/* Marker line */}
				<line
					x1={markerX}
					y1={10}
					x2={markerX}
					y2={curveBottom + 52}
					stroke="#22b8cf"
					strokeWidth="5"
				/>
				{/* Marker label */}
				<text
					x={markerX > width - 85 ? markerX - 6 : markerX + 6}
					y={14}
					textAnchor={markerX > width - 85 ? 'end' : 'start'}
					className="customer-report-mini-z-chart__marker-label"
				>
					<tspan
						x={markerX > width - 85 ? markerX - 6 : markerX + 6}
						dy="0"
					>
						{`Z: ${z.toFixed(2)}`}
					</tspan>
					<tspan
						x={markerX > width - 85 ? markerX - 6 : markerX + 6}
						dy="12"
					>
						{`T: ${displayT.toFixed(1)}`}
					</tspan>
				</text>

				{/* --- T-score axis line --- */}
				<line
					x1={leftPad}
					y1={curveBottom + 26}
					x2={width - rightPad}
					y2={curveBottom + 26}
					stroke="#6b7280"
					strokeWidth="1.5"
				/>

				{/* T-score ticks + labels */}
				{tTicks.map((tick) => {
					const x = xForZ(tick.z);
					return (
						<g key={`t-tick-${tick.t}`}>
							<line
								x1={x}
								y1={curveBottom + 20}
								x2={x}
								y2={curveBottom + 32}
								stroke="#6b7280"
								strokeWidth="1"
							/>
							<text
								x={x}
								y={curveBottom + 46}
								textAnchor="middle"
								className="customer-report-mini-z-chart__axis-label"
							>
								{tick.t}
							</text>
						</g>
					);
				})}

				{/* Axis title */}
				<text
					x={width / 2}
					y={height - 10}
					textAnchor="middle"
					className="customer-report-mini-z-chart__axis-title"
				>
					{strings.testView.tScoreLabel}
				</text>
			</svg>
		</div>
	);
};

const SubtestOverview = ({subtest}) => {
	const assessment = getZAssessment(subtest.zScore);
	const assessmentClass = getZAssessmentClass(subtest.zScore);

	return (
		<div className="customer-report-subtest-overview">
			<div className="customer-report-subtest-overview__chart">
				<MiniZChart zScore={subtest.zScore} tScore={subtest.tScore} />
			</div>

			<div className="customer-report-subtest-overview__stats">
				<div>
					<strong>{strings.common.rapoang}:</strong>{' '}
					{valueOrDash(subtest.totalCorrect)}
				</div>
				<div>
					<strong>{strings.common.zpoang}:</strong> {valueOrDash(subtest.zScore)}
				</div>
				<div>
					<strong>{strings.common.tpoang}:</strong> {valueOrDash(subtest.tScore)}
				</div>
				<div
					className={`customer-report-subtest-overview__assessment ${assessmentClass}`}
				>
					{assessment}
				</div>
			</div>
		</div>
	);
};

// eslint-disable-next-line react/no-multi-comp
const CustomerReportPrint = ({
	enabled = false,
	preview = true,
	testResult,
	testType,
	completeName,
	birthDate,
	youthGrade,
	reportNotes,
	summaryChartImage = null,
	options = {},
}) => {
	if (!enabled || !testResult) {
		return null;
	}

	const subtests = testResult.subtests || [];
	const reportOptions = {
		...defaultReportOptions,
		...options,
	};

	return (
		<div
			className={
				preview
					? 'customer-report-preview'
					: 'customer-result-print-container'
			}
		>
			{preview && (
				<div className="customer-report-preview__notice">
					{strings.testView.nyRapportlayout}
				</div>
			)}

			<div className="customer-report-print">
				{reportOptions.includeCover && (
					<section className="customer-report-page customer-report-cover">
						<div className="customer-report-cover__header">
							<div className="customer-report-cover__brand">
								<img
									src={Logo}
									alt={strings.common.dysmateLogoAlt}
									className="customer-report-cover__logo"
								/>
							</div>

							<div className="customer-report-cover__title-wrap">
								<h1>{getReportTitle(testType)}</h1>
								<p className="customer-report-cover__subtitle">
									{strings.testView.resultsReport}
								</p>
							</div>
						</div>

						<table className="customer-report-meta">
							<tbody>
								<tr>
									<th>{strings.common.namn}</th>
									<td>{completeName}</td>
								</tr>
								<tr>
									<th>{strings.candidates.birthDate}</th>
									<td>{birthDate || '-'}</td>
								</tr>
								<tr>
									<th>{strings.candidates.educationagegroup}</th>
									<td>
										{youthGrade
											? strings.formatString(strings.common.arskursNum, youthGrade)
											: '-'}
									</td>
								</tr>
								<tr>
									<th>{strings.testManagement.completiondate}</th>
									<td>
										{testResult.completedAt
											? moment(
													testResult.completedAt
												).format('YYYY-MM-DD')
											: '-'}
									</td>
								</tr>
							</tbody>
						</table>
					</section>
				)}
				{reportOptions.includeSummary && (
					<section className="customer-report-page customer-report-summary-section">
							<h2>{strings.testView.summary}</h2>
						<table className="customer-report-table customer-report-summary-table">
							<thead>
								<tr>
									<th>{strings.testView.deltest}</th>
									<th>{strings.common.points}</th>
									<th>{strings.testView.zScoreLabel}</th>
									<th>{strings.common.percentil}</th>
									<th>{strings.testView.tScoreLabel}</th>
								</tr>
							</thead>
							<tbody>
								{subtests.map((subtest) => (
									<tr key={subtest.id}>
										<td>
											{getSubtestName(subtest, testType)}
										</td>
										<td>
											{valueOrDash(subtest.totalCorrect)}
										</td>
										<td>{valueOrDash(subtest.zScore)}</td>
										<td>
											{valueOrDash(
												subtest.percentileScore
											)}
										</td>
										<td>{valueOrDash(subtest.tScore)}</td>
									</tr>
								))}
							</tbody>
						</table>
						<div className="summary-chart">
							{summaryChartImage ? (
								<img
									src={summaryChartImage}
									alt=""
									style={{width: '100%', display: 'block'}}
								/>
							) : (
								<HorizontalBarChart
									subtests={subtests}
									testType={testType}
								/>
							)}
						</div>
						<div className="explanations">
							<div className="explanation">
								<div className="explanation__figure negative" />
								<p className="explanation__text">
									{strings.formatString(
										strings.testView.zscorered,
										<em>{strings.common.under}</em>
									)}
								</p>
							</div>
							<div className="explanation">
								<div className="explanation__figure neutral" />
								<p className="explanation__text">
									{strings.testView.zscoreblue}
								</p>
							</div>
							<div className="explanation">
								<div className="explanation__figure positive" />
								<p className="explanation__text">
									{strings.formatString(
										strings.testView.zscoregreen,
										<em>{strings.common.over}</em>
									)}
								</p>
							</div>
						</div>
					</section>
				)}
				{/*
	Detailed answer sections.
	Still preview-only for now.
*/}
				{reportOptions.includeDetailedAnswers &&
					(testResult.subtests || []).map((subtest) => (
						<section
							className="customer-report-page customer-report-answer-section"
							key={`answers-${subtest.id}`}
						>
							<div className="customer-report-answer-section__intro">
								<h2>
									{getSubtestName(subtest, testType)} - svar
								</h2>

								<p className="customer-report-score-line">
									{strings.common.points}: {valueOrDash(subtest.totalCorrect)}.
									{strings.testView.zScoreLabel}: {valueOrDash(subtest.zScore)}.
									{strings.common.percentil}:{' '}
									{valueOrDash(subtest.percentileScore)}.
									{strings.testView.tScoreLabel}: {valueOrDash(subtest.tScore)}.
								</p>
							</div>

							{reportOptions.includeSubtestCharts && (
								<SubtestOverview subtest={subtest} />
							)}

							<ReportAnswerTable
								subtest={subtest}
								testType={testType}
							/>
						</section>
					))}
				{reportOptions.includeComments && reportNotes && (
					<section className="customer-report-page">
						<h2>{strings.testView.comments}</h2>
						<p className="customer-report-notes">{reportNotes}</p>
					</section>
				)}
			</div>
		</div>
	);
};

export default CustomerReportPrint;
